import { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import { useUser } from '@/contexts/UserContext';
import { APIRoutes } from '@/services/api.routes';
import { INTRO_VIDEO_CONFIG } from '@/constants/intro-videos';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';

interface IntroVideoModalProps {
  type: TradingRecommendationType;
}

interface BunnyStreamMessage {
  channel?: string;
  event?: string;
  status?: {
    ended?: boolean;
    currentTime?: number;
    duration?: number;
  };
}

export default function IntroVideoModal({ type }: IntroVideoModalProps) {
  const { user, updateUser } = useUser();
  const config = INTRO_VIDEO_CONFIG[type];

  const [videoEnded, setVideoEnded] = useState(false);
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isOpen = user !== null
    && user.frontendFlags[config.flagKey] === null
    && user.featureConfig[config.featureKey] === true
    && !user.featureConfig.communityMember;

  const handleVideoEnded = useCallback(() => {
    setVideoEnded(true);
  }, []);

  // Listen for Bunny Stream protocol messages and detect video end
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the Bunny CDN origin
      if (!event.origin.includes('mediadelivery.net')) {
        return;
      }

      try {
        const data: BunnyStreamMessage = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // Only process Bunny Stream protocol messages
        if (data.channel !== 'bunnystream') return;

        if (data.event === 'ended') {
          handleVideoEnded();
          return;
        }

        // Also detect near-end via timeupdate as safety net
        if (data.event === 'timeupdate' && data.status !== undefined) {
          const { currentTime, duration } = data.status;
          if (currentTime !== undefined && duration !== undefined && duration > 0) {
            if (currentTime / duration >= 0.95) {
              handleVideoEnded();
            }
          }
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);

    // Send 'activate' command to the iframe to start receiving events
    const sendActivate = () => {
      const contentWindow = iframe.contentWindow;
      if (contentWindow !== null) {
        contentWindow.postMessage({ command: 'activate' }, '*');
      }
    };

    const handleIframeLoad = () => {
      // Small delay to ensure the Bunny player is fully initialized
      setTimeout(sendActivate, 500);
    };

    iframe.addEventListener('load', handleIframeLoad);

    // If iframe is already loaded, send activate now
    if (iframe.contentDocument?.readyState === 'complete') {
      handleIframeLoad();
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [isOpen, handleVideoEnded]);

  const handleClose = async () => {
    if (!videoEnded || saving) return;

    setSaving(true);
    try {
      const updatedUser = await APIRoutes.updateCurrentUser({
        frontendFlags: { [config.flagKey]: true },
      });
      updateUser(updatedUser);
    } catch (err) {
      console.error('Failed to update intro flag:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={config.title}
      icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
      maxWidth="4xl"
      closeDisabled={!videoEnded || saving}
    >
      <div className="p-6">
        <div className="relative overflow-hidden rounded-lg" style={{ paddingTop: '56.25%' }}>
          <iframe
            ref={iframeRef}
            src={config.bunnyEmbedUrl}
            loading="lazy"
            style={{
              border: 0,
              position: 'absolute',
              top: 0,
              height: '100%',
              width: '100%',
            }}
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
            allowFullScreen={true}
            title={config.title}
          />
        </div>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          Bitte schaue das Video bis zum Ende, um fortzufahren.
        </p>

        <div className="mt-4 flex justify-center">
          <button
            onClick={handleClose}
            disabled={!videoEnded || saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
          >
            {saving ? 'Wird gespeichert...' : 'Weiter'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
